import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Activity, ArrowDown, RefreshCw, TrendingUp, TrendingDown, ShieldCheck,
} from 'lucide-react';

/**
 * iter-221f — Trial Gate Funnel admin panel
 *
 * Reads `/api/admin/telemetry/trial-gate-funnel` and renders a per-week
 * conversion funnel: trials started → gate hits → recovered/dropped.
 * Highlights the recovery rate and surfaces the worst week.
 */
export default function TrialGateFunnelPanel() {
  const { apiCall } = useAuth();
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const out = await apiCall(`/api/admin/telemetry/trial-gate-funnel?weeks=${weeks}`);
      setData(out);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [weeks]); // eslint-disable-line

  const totals = data?.totals || {};
  const recovery = data?.overall_recovery_rate_pct;
  const worstWeek = (data?.weeks || []).reduce((acc, w) => {
    if (!w.gate_hits) return acc;
    if (!acc || w.recovery_rate_pct < acc.recovery_rate_pct) return w;
    return acc;
  }, null);

  return (
    <Card data-testid="trial-gate-funnel-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" />
              Trial Gate Funnel
            </CardTitle>
            <CardDescription>
              Conversion across vendors hitting the trial wall — recovery vs drop-off, by ISO week.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(weeks)} onValueChange={(v) => setWeeks(Number(v))}>
              <SelectTrigger className="h-9 w-32" data-testid="funnel-weeks-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">Last 4 weeks</SelectItem>
                <SelectItem value="8">Last 8 weeks</SelectItem>
                <SelectItem value="12">Last 12 weeks</SelectItem>
                <SelectItem value="26">Last 26 weeks</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="funnel-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        {data && (
          <div className="mt-3 flex items-center gap-2">
            {data.posthog_forwarding ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" data-testid="funnel-posthog-on">
                Forwarding to PostHog
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-gray-500">
                Internal only — set POSTHOG_API_KEY env var to fan out
              </Badge>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Top-line KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Trials started" value={totals.trials_started || 0} testid="funnel-kpi-trials" />
          <Kpi label="Gate hits" value={totals.gate_hits || 0} accent="amber" testid="funnel-kpi-gate" />
          <Kpi
            label="Recovered → paid"
            value={totals.recovered_picked_plan || 0}
            accent="emerald"
            testid="funnel-kpi-recovered"
          />
          <Kpi
            label="Recovery rate"
            value={recovery == null ? '—' : `${recovery}%`}
            accent={recovery == null ? 'gray' : recovery >= 50 ? 'emerald' : recovery >= 25 ? 'amber' : 'rose'}
            testid="funnel-kpi-rate"
          />
        </div>

        {/* Sankey-ish horizontal flow */}
        {totals.trials_started > 0 && (
          <div className="rounded-lg border bg-gray-50/50 p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Cumulative flow (last {weeks} weeks)
            </p>
            <div className="space-y-2">
              <FlowRow label="Trials started" count={totals.trials_started} max={totals.trials_started} cls="bg-indigo-500" />
              <FlowRow
                label="Hit the gate"
                count={totals.gate_hits}
                max={totals.trials_started}
                cls="bg-amber-500"
              />
              <FlowRow
                label="Picked a plan"
                count={totals.recovered_picked_plan}
                max={totals.gate_hits || 1}
                cls="bg-emerald-500"
              />
              <FlowRow
                label="Dropped off"
                count={totals.dropped_off}
                max={totals.gate_hits || 1}
                cls="bg-rose-500"
              />
            </div>
          </div>
        )}

        {worstWeek && (
          <div className="rounded-md border-l-4 border-rose-400 bg-rose-50 p-3 flex items-start gap-2">
            <TrendingDown className="h-4 w-4 text-rose-500 mt-0.5" />
            <div className="text-sm">
              <p className="text-rose-900 font-medium">
                Worst week: <span className="font-mono">{worstWeek.week}</span> · only{' '}
                {worstWeek.recovery_rate_pct}% recovered
              </p>
              <p className="text-rose-700 text-xs">
                {worstWeek.gate_hits} hit the gate, {worstWeek.recovered_picked_plan} converted,{' '}
                {worstWeek.dropped_off} dropped.
              </p>
            </div>
          </div>
        )}

        {/* Per-week table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="funnel-weekly-table">
            <thead className="border-b border-gray-200 bg-gray-50/50">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2">Week</th>
                <th className="px-3 py-2 text-right">Trials</th>
                <th className="px-3 py-2 text-right">Gate hits</th>
                <th className="px-3 py-2 text-right">Recovered</th>
                <th className="px-3 py-2 text-right">Dropped</th>
                <th className="px-3 py-2 text-right">Recovery %</th>
              </tr>
            </thead>
            <tbody>
              {(!data?.weeks || data.weeks.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-gray-400 text-xs">
                    No telemetry events yet — events will start populating once vendors interact with the gate.
                  </td>
                </tr>
              )}
              {(data?.weeks || []).map((w) => (
                <tr key={w.week} className="border-b border-gray-100" data-testid={`funnel-row-${w.week}`}>
                  <td className="px-3 py-2 font-mono text-xs">{w.week}</td>
                  <td className="px-3 py-2 text-right">{w.trials_started}</td>
                  <td className="px-3 py-2 text-right text-amber-700">{w.gate_hits}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                    {w.recovered_picked_plan}
                  </td>
                  <td className="px-3 py-2 text-right text-rose-700">{w.dropped_off}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {w.recovery_rate_pct == null ? '—' : `${w.recovery_rate_pct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

const ACCENT_COLORS = {
  amber: 'text-amber-700',
  emerald: 'text-emerald-700',
  rose: 'text-rose-700',
  gray: 'text-gray-500',
};

const Kpi = ({ label, value, accent = 'gray', testid }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testid}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-0.5 ${ACCENT_COLORS[accent] || 'text-gray-800'}`}>
      {value}
    </p>
  </div>
);

const FlowRow = ({ label, count, max, cls }) => {
  const pct = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 text-xs text-gray-600 text-right">{label}</div>
      <div className="flex-1 h-7 bg-white rounded-md overflow-hidden border border-gray-200">
        <div
          className={`h-full ${cls} flex items-center justify-end pr-2 transition-all duration-300`}
          style={{ width: count > 0 ? `${pct}%` : '0%' }}
        >
          {count > 0 && (
            <span className="text-[11px] font-bold text-white">
              {count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
